import { Info } from 'lucide-preact'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { HubSpotMappingConfig } from '@/types/hubspot'

interface ExistingLeadBehaviorStepProps {
    config: HubSpotMappingConfig
    onChange: (config: HubSpotMappingConfig) => void
}

export function ExistingLeadBehaviorStep({ config, onChange }: ExistingLeadBehaviorStepProps) {
    const overwriteRules = config.ExistingLeadBehavior.OverwriteRules

    const toggleOverwrite = (index: number) => {
        const updated = [...overwriteRules]
        updated[index] = { ...updated[index], OverwriteExisting: !updated[index].OverwriteExisting }
        onChange({
            ...config,
            ExistingLeadBehavior: { ...config.ExistingLeadBehavior, OverwriteRules: updated }
        })
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Existing Lead Behavior</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Configure overwrite behavior when a contact already exists in HubSpot.
                </p>
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <p>When a matching lead is found, update existing contact properties. Choose which fields should be overwritten with new data when an existing record is identified by email address.</p>
            </div>

            {/* Overwrite rules table */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <h4 className="font-medium text-sm text-slate-900 dark:text-white">Field Overwrite Rules</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Toggle to allow incoming data to replace existing values.</p>
                </div>

                {/* Column headers */}
                <div className="grid grid-cols-12 gap-3 px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800">
                    <div className="col-span-4">Field Name</div>
                    <div className="col-span-3 text-center">Overwrite Existing Value?</div>
                    <div className="col-span-5">Notes</div>
                </div>

                {/* Rows */}
                {overwriteRules.map((rule, index) => (
                    <div
                        key={rule.FieldName}
                        className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                    >
                        <div className="col-span-4">
                            <span className="text-sm font-medium text-slate-900 dark:text-white">{rule.FieldName}</span>
                        </div>
                        <div className="col-span-3 flex justify-center">
                            <ToggleRow
                                checked={rule.OverwriteExisting}
                                onChange={() => toggleOverwrite(index)}
                                ariaLabel={`Overwrite existing value for ${rule.FieldName}`}
                                size="sm"
                            />
                        </div>
                        <div className="col-span-5">
                            {rule.DefaultOverwrite && rule.OverwriteExisting && (
                                <span className="inline-flex px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-md bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                    Recommended On
                                </span>
                            )}
                            {!rule.OverwriteExisting && !rule.DefaultOverwrite && !rule.Notes && (
                                <span className="text-xs text-slate-400 dark:text-slate-500">Default off</span>
                            )}
                            {rule.Notes && !rule.DefaultOverwrite && (
                                <span className="text-xs text-slate-400 dark:text-slate-500">{rule.Notes}</span>
                            )}
                            {rule.Notes && rule.DefaultOverwrite && rule.OverwriteExisting && (
                                <span className="text-xs text-slate-400 dark:text-slate-500 ml-2">{rule.Notes}</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
